import { Box, Column, Tooltip } from "@hightouch/htui";
import { useNavWidth } from "src/contexts/nav-width-context";
import { SmallLogo } from "./logos";
import { SidebarOpenIcon, SidebarClosedIcon } from "src/ui/icons";
import { useUser } from "src/contexts/user-context";
import * as analytics from "src/lib/analytics";
import { RouterLink } from "src/router";

/**
 * Button to toggle the sidebar navigation.
 *
 * When nav is collapsed, the button displays the small hightouch logomark
 * and on hover of itself, or over the collapsed nav deadspace, the sidebar icon is shown in place of the logomark.
 *
 * On sm and md screens, you cannot expand the sidebar,
 * and the logomark acts as a home link instead.
 */
export function NavToggleButton({
  showHoverState = false,
}: {
  showHoverState?: boolean;
}) {
  const { canNavOpen, isNavOpen, setIsNavOpen } = useNavWidth();
  const { workspace, user } = useUser();

  const orgLogo = user?.organization?.logo_url;

  function handleClick() {
    if (canNavOpen) {
      analytics.track("Navigation: Toggle sidebar", {
        workspace_id: workspace?.id,
        nav_state: isNavOpen ? "closed" : "open",
      });
      setIsNavOpen((prev) => !prev);
    }
  }

  // Nav cannot be toggled on mobile screens.
  // Display the logomark as a home link instead
  if (!canNavOpen) {
    return (
      <RouterLink to="/" aria-label="Home">
        <SmallLogo />
      </RouterLink>
    );
  }

  return (
    <Tooltip
      message={`${isNavOpen ? "Close" : "Open"} sidebar`}
      keyboardShortcut="mod+/"
      placement="right"
    >
      <Column
        as="button"
        aria-label={`${isNavOpen ? "Close" : "Open"} navigation sidebar`}
        className="group"
        boxSize={orgLogo && !isNavOpen ? 10 : 8}
        borderRadius="lg"
        color="text.tertiary"
        align="center"
        justify="center"
        flexShrink={0}
        userSelect="none"
        position="relative"
        transitionProperty="background-color, color"
        transitionDuration="fast"
        transitionTimingFunction="easeOutQuad"
        _hover={{
          bg: "gray.200",
          color: "text.tertiary",
        }}
        _active={{
          bg: "transparent", // Avoid hover state bg flash when toggling nav
        }}
        _focusVisible={{
          outline: "none",
          boxShadow: "outline",
        }}
        onClick={handleClick}
      >
        {isNavOpen ? (
          <Box as={SidebarOpenIcon} boxSize={5} flexShrink={0} />
        ) : (
          <Column align="center" justify="center" pos="absolute" inset={0}>
            <Box
              as={SidebarClosedIcon}
              position="absolute"
              color="inherit"
              flexShrink={0}
              boxSize={5}
              showArrow={showHoverState}
              transitionProperty="opacity"
              transitionDuration="fast"
              transitionTimingFunction="easeOutQuad"
              opacity={showHoverState ? 1 : 0}
              visibility={showHoverState ? "visible" : "hidden"}
              _groupHover={{
                opacity: 1,
                visibility: "visible",
              }}
            />

            <SmallLogo
              position="absolute"
              flexShrink={0}
              transitionProperty="opacity"
              transitionDuration="fast"
              transitionTimingFunction="easeOutQuad"
              opacity={showHoverState ? 0 : 1}
              visibility={showHoverState ? "hidden" : "visible"}
              _groupHover={{
                opacity: 0,
                visibility: "hidden",
              }}
            />
          </Column>
        )}
      </Column>
    </Tooltip>
  );
}
